const money = (amount, currency = 'GBP') => new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(amount / 100);

export function getBundleState(config, selectedElectrolyteId) {
  const allProducts = Array.isArray(config.products) ? config.products : [];
  const electrolyte = config.mode === 'kit'
    ? allProducts.find((product) => ['citrus', 'orange'].includes(product.role) && String(product.id) === String(selectedElectrolyteId))
    : allProducts.find((product) => product.role === 'citrus');
  const second = allProducts.find((product) => product.role === (config.mode === 'kit' ? 'creatine' : 'orange'));
  const products = [electrolyte, second];
  const validProducts = products.every((product) => product && /^\d+$/.test(String(product.id)) && product.available === true && Number.isInteger(product.price) && product.price > 0)
    && String(electrolyte.id) !== String(second.id);
  const retailTotal = validProducts ? products.reduce((sum, product) => sum + product.price, 0) : 0;
  const expectedSubtotal = config.mode === 'kit' ? 5498 : 5998;
  const discountAmount = config.mode === 'kit' ? 999 : 1499;
  const discountCode = config.mode === 'kit' ? 'AMPMKIT4499' : 'DISCOVERY4499';
  const ready = Boolean(['kit', 'discovery'].includes(config.mode) && validProducts && config.offerVerified === true && config.currency === 'GBP'
    && config.offerPrice === 4499 && config.expectedSubtotal === expectedSubtotal && config.discountAmount === discountAmount
    && config.discountCode === discountCode && retailTotal === expectedSubtotal);
  return { ready, products, retailTotal, price: ready ? retailTotal - discountAmount : null, saving: ready ? discountAmount : 0 };
}

export function buildBundleCheckoutUrl(config, selectedElectrolyteId, origin) {
  const state = getBundleState(config, selectedElectrolyteId);
  if (!state.ready) throw new Error('This bundle offer is currently unavailable. Please shop the individual products or try again later.');
  const cart = new URL(config.cartUrl, origin);
  if (cart.origin !== new URL(origin).origin || !/\/cart\/?$/.test(cart.pathname)) throw new Error('The checkout link is unavailable. Please try again later.');
  const checkout = new URL(cart.pathname.replace(/\/$/, '') + '/' + state.products.map((product) => product.id + ':1').join(','), origin);
  checkout.searchParams.set('discount', config.discountCode);
  checkout.searchParams.set('ref', String(config.offerId || 'ampm_bundle'));
  return checkout.href;
}

export function mountOneTimeBundle(section) {
  if (section.dataset.bundleReady === 'true') return;
  const configElement = section.querySelector('[data-bundle-config]');
  const checkout = section.querySelector('[data-bundle-checkout]');
  if (!configElement || !checkout) return;
  let config;
  try { config = JSON.parse(configElement.textContent); }
  catch { checkout.removeAttribute('href'); checkout.setAttribute('aria-disabled', 'true'); return; }
  section.dataset.bundleReady = 'true';
  if (config.suppressPopups) document.body.classList.add('ampm-paid-route--no-promos');
  const select = section.querySelector('[data-bundle-flavour]');
  if (select) select.disabled = false;
  const error = section.querySelector('[data-bundle-error]');
  const status = section.querySelector('[data-bundle-status]');
  const selectedId = () => select?.value || config.products?.find((product) => product.role === 'citrus')?.id;
  const text = (selector, value) => section.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
  const emit = (name, detail = {}) => {
    const payload = { offer_id: config.offerId, purchase_type: 'one_time', ...detail };
    window.dispatchEvent(new CustomEvent(name, { detail: payload }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
  };
  const update = () => {
    const state = getBundleState(config, selectedId());
    checkout.setAttribute('aria-disabled', String(!state.ready));
    if (state.ready) {
      try { checkout.href = buildBundleCheckoutUrl(config, selectedId(), window.location.origin); }
      catch { checkout.removeAttribute('href'); checkout.setAttribute('aria-disabled', 'true'); }
    } else checkout.removeAttribute('href');
    text('[data-bundle-price]', state.ready ? money(state.price, config.currency) : 'Currently unavailable');
    text('[data-bundle-badge]', state.ready ? 'Save ' + money(state.saving, config.currency) + ' when bought together' : 'One-time bundle');
    text('[data-bundle-retail]', money(state.retailTotal, config.currency));
    text('[data-bundle-saving]', money(state.saving, config.currency));
    text('[data-bundle-checkout]', state.ready ? config.buttonLabel + ' · ' + money(state.price, config.currency) : 'Bundle currently unavailable');
    section.querySelector('[data-bundle-comparison]').hidden = !state.ready;
    section.querySelector('[data-bundle-unavailable]').hidden = state.ready;
    const electrolyte = state.products[0];
    if (electrolyte) {
      const image = section.querySelector('[data-bundle-electrolyte-image]');
      if (image && electrolyte.image && select) { image.src = electrolyte.image; image.removeAttribute('srcset'); image.alt = electrolyte.title; }
      text('[data-bundle-electrolyte-label]', electrolyte.label);
    }
    return state;
  };
  select?.addEventListener('change', () => {
    error.hidden = true;
    const state = update();
    status.textContent = state.products[0] ? 'Your bundle includes ' + state.products[0].label + '.' : 'Please choose an available flavour.';
    emit('ampm_flavour_selection', { product: state.products[0]?.title });
  });
  checkout.addEventListener('click', (event) => {
    const state = update();
    if (!state.ready || !checkout.hasAttribute('href')) {
      event.preventDefault();
      error.textContent = 'This bundle offer is currently unavailable. Please shop the individual products or try again later.';
      error.hidden = false;
      error.focus();
      return;
    }
    emit('ampm_begin_checkout', { currency: config.currency, value: state.price / 100, discount_code: config.discountCode, item_count: 2, items: state.products.map((product) => ({ item_id: String(product.id), item_name: product.title, quantity: 1 })) });
  });
  window.addEventListener('pageshow', () => { error.hidden = true; update(); });
  update();
  emit('ampm_offer_view', { bundle: true });
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-one-time-bundle]').forEach(mountOneTimeBundle);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-one-time-bundle]').forEach(mountOneTimeBundle));
}
